import { HCDatepicker } from '@chiroup/components/datepicker/HCDatepicker';
import { hasProperty } from '@chiroup/core/functions/hasProperty';
import { DateValue, SurveyOption } from '@chiroup/core/types/Survey.type';
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import {
  SoftCheckboxMarker,
  SoftOptionCard,
  SoftPillButton,
  SoftQuestionShell,
  SoftRadioMarker,
  SoftTextArea,
  SoftTextField,
  inputClass,
  ringStyle,
} from './primitives';
import { SoftQuestionProps, surveyVerbiage } from './types';

// Built-in question type renderers. Each one composes the Soft Card
// primitives — so a new visual variation can either:
//   (a) Override individual primitives in `primitives.tsx` to ripple
//       through every type at once, or
//   (b) Replace one of these components and pass it via the
//       `renderers` prop on `SoftSurveyQuestion`.

// -- Radio (single-select) ------------------------------------------------

export const SoftRadioQuestion: React.FC<SoftQuestionProps> = props => {
  const {
    question,
    progress,
    language,
    primaryColor,
    disabled,
    hidden,
    showPrevious,
    onBack,
    onSelect,
  } = props;
  const v = surveyVerbiage[language];
  const includeOther = question.typeOptions?.includeOther;

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [otherValue, setOtherValue] = useState('');
  const otherRef = useRef<HTMLInputElement>(null);

  const handlePick = async (id: string) => {
    if (id === 'other') {
      setSelectedId('other');
      setTimeout(() => otherRef.current?.focus(), 0);

      return;
    }

    setSelectedId(id);
    await onSelect({ type: 'radio', value: id });
  };

  const submitOther = async () => {
    if (!otherValue.trim()) return;
    await onSelect({ type: 'radio', value: 'other', otherValue: otherValue.trim() });
  };

  return (
    <SoftQuestionShell
      question={question}
      progress={progress}
      language={language}
      primaryColor={primaryColor}
      hidden={hidden}
      showPrevious={showPrevious}
      onBack={onBack}
    >
      <ul role="list" className="flex flex-col gap-2.5">
        {(question.options || []).map((opt: SurveyOption, i: number) => {
          const id = String(opt?.id ?? i);

          return (
            <li key={id}>
              <SoftOptionCard
                primaryColor={primaryColor}
                disabled={disabled}
                onClick={() => handlePick(id)}
                testCy={opt.display}
              >
                <SoftRadioMarker primaryColor={primaryColor} selected={selectedId === id} />
                <span className="flex-1 text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                  {opt.display}
                </span>
              </SoftOptionCard>
            </li>
          );
        })}
        {includeOther && (
          <li>
            {selectedId === 'other' ? (
              <div className="rounded-2xl px-4 py-3.5 portal-card flex flex-col gap-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  {v.other}
                </p>
                <SoftTextField
                  ref={otherRef}
                  primaryColor={primaryColor}
                  name={`question-${question.id}-other`}
                  value={otherValue}
                  onChange={e => setOtherValue(e.target.value)}
                  placeholder={v.otherPlaceholder}
                  disabled={disabled}
                />
                <SoftPillButton
                  primaryColor={primaryColor}
                  disabled={disabled || !otherValue.trim()}
                  onClick={submitOther}
                >
                  {v.next}
                </SoftPillButton>
              </div>
            ) : (
              <SoftOptionCard
                primaryColor={primaryColor}
                disabled={disabled}
                onClick={() => handlePick('other')}
              >
                <SoftRadioMarker primaryColor={primaryColor} />
                <span className="flex-1 text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                  {v.other}
                </span>
              </SoftOptionCard>
            )}
          </li>
        )}
      </ul>
    </SoftQuestionShell>
  );
};

// -- Checkbox (multi-select) ----------------------------------------------

export const SoftCheckboxQuestion: React.FC<SoftQuestionProps> = props => {
  const {
    question,
    progress,
    language,
    primaryColor,
    disabled,
    hidden,
    showPrevious,
    onBack,
    onSelect,
  } = props;
  const v = surveyVerbiage[language];
  const includeOther = question.typeOptions?.includeOther;

  const [val, setVal] = useState<{ [key: string]: string }>({});
  const [showOther, setShowOther] = useState(false);
  const [otherValue, setOtherValue] = useState('');
  const otherRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (showOther) otherRef.current?.focus();
  }, [showOther]);

  const isChecked = (id: string) => hasProperty(val, id);

  const toggle = (opt: SurveyOption) => {
    const id = String(opt.id);

    setVal(prev => {
      if (hasProperty(prev, id)) {
        const { [id]: _drop, ...rest } = prev;

        return rest;
      }

      return { ...prev, [id]: opt.data?.askForInput ? '' : id };
    });
  };

  const setOptionInput = (id: string, value: string) => {
    setVal(prev => ({ ...prev, [id]: value }));
  };

  const toggleOther = () => {
    setShowOther(prev => !prev);

    if (showOther) setOtherValue('');
  };

  const handleSubmit = async () => {
    const valueToSend = Object.keys(val);
    const dataForSend = Object.entries(val)
      .filter(([key, value]) => key !== value)
      .reduce((obj: { [key: string]: string }, [key, value]) => {
        obj[key] = value;

        return obj;
      }, {});

    await onSelect({
      type: 'checkbox',
      value: valueToSend,
      otherValue: showOther ? otherValue : undefined,
      data: dataForSend,
    });
    setVal({});
    setShowOther(false);
    setOtherValue('');
  };

  const anySelected = Object.keys(val).length > 0 || (showOther && !!otherValue.trim());

  return (
    <SoftQuestionShell
      question={question}
      progress={progress}
      language={language}
      primaryColor={primaryColor}
      hidden={hidden}
      showPrevious={showPrevious}
      onBack={onBack}
    >
      <ul role="list" className="flex flex-col gap-2.5">
        {(question.options || []).map((opt: SurveyOption, i: number) => {
          const id = String(opt?.id ?? i);
          const checked = isChecked(id);

          return (
            <li key={id}>
              <SoftOptionCard
                primaryColor={primaryColor}
                disabled={disabled}
                onClick={() => toggle(opt)}
                alignTop
                testCy={opt.display}
              >
                <SoftCheckboxMarker primaryColor={primaryColor} checked={checked} />
                <div className="flex-1 min-w-0">
                  <span className="block text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                    {opt.display}
                  </span>
                  {opt.data?.askForInput && checked && (
                    <input
                      type="text"
                      name={`question-${question.id}-input-${id}`}
                      value={typeof val[id] === 'string' ? val[id] : ''}
                      onChange={e => setOptionInput(id, e.target.value)}
                      onClick={e => e.stopPropagation()}
                      placeholder={opt.data?.inputPlaceholder}
                      disabled={disabled}
                      className={`mt-2 ${inputClass}`}
                      style={ringStyle(primaryColor)}
                    />
                  )}
                </div>
              </SoftOptionCard>
            </li>
          );
        })}
        {includeOther && (
          <li>
            <div className="rounded-2xl px-4 py-3.5 portal-card flex flex-col gap-2">
              <button
                type="button"
                onClick={toggleOther}
                disabled={disabled}
                className="flex items-center gap-3 text-left disabled:opacity-50"
              >
                <SoftCheckboxMarker primaryColor={primaryColor} checked={showOther} />
                <span className="flex-1 text-sm sm:text-base font-medium text-gray-900 dark:text-gray-100">
                  {v.other}
                </span>
              </button>
              {showOther && (
                <SoftTextField
                  ref={otherRef}
                  primaryColor={primaryColor}
                  name={`question-${question.id}-other`}
                  value={otherValue}
                  onChange={e => setOtherValue(e.target.value)}
                  placeholder={v.otherPlaceholder}
                  disabled={disabled}
                />
              )}
            </div>
          </li>
        )}
      </ul>

      <SoftPillButton
        primaryColor={primaryColor}
        disabled={disabled}
        onClick={handleSubmit}
        testCy={anySelected ? 'next-button' : 'none-of-the-above-button'}
      >
        {anySelected ? v.next : v.noneOfTheAbove}
      </SoftPillButton>
    </SoftQuestionShell>
  );
};

// -- Text input -----------------------------------------------------------

export const SoftInputQuestion: React.FC<SoftQuestionProps> = props => {
  const {
    question,
    progress,
    language,
    primaryColor,
    disabled,
    hidden,
    showPrevious,
    onBack,
    onSelect,
  } = props;
  const v = surveyVerbiage[language];
  const [val, setVal] = useState('');

  const handleSubmit = async () => {
    if (!val.trim()) return;
    await onSelect({ type: 'input', value: val.trim() });
    setVal('');
  };

  return (
    <SoftQuestionShell
      question={question}
      progress={progress}
      language={language}
      primaryColor={primaryColor}
      hidden={hidden}
      showPrevious={showPrevious}
      onBack={onBack}
    >
      <SoftTextField
        primaryColor={primaryColor}
        name={`question-${question.id}`}
        value={val}
        onChange={e => setVal(e.target.value)}
        disabled={disabled}
        autoFocus
      />
      <SoftPillButton
        primaryColor={primaryColor}
        disabled={disabled || !val.trim()}
        onClick={handleSubmit}
      >
        {v.next}
      </SoftPillButton>
    </SoftQuestionShell>
  );
};

// -- Textarea -------------------------------------------------------------

export const SoftTextareaQuestion: React.FC<SoftQuestionProps> = props => {
  const {
    question,
    progress,
    language,
    primaryColor,
    disabled,
    hidden,
    showPrevious,
    onBack,
    onSelect,
  } = props;
  const v = surveyVerbiage[language];
  const forceAnswer = question.typeOptions?.forceAnswer ?? true;
  const [val, setVal] = useState('');

  const handleSubmit = async () => {
    if (forceAnswer && !val.trim()) return;
    await onSelect({ type: 'input', value: val.trim() });
    setVal('');
  };

  return (
    <SoftQuestionShell
      question={question}
      progress={progress}
      language={language}
      primaryColor={primaryColor}
      hidden={hidden}
      showPrevious={showPrevious}
      onBack={onBack}
    >
      <SoftTextArea
        primaryColor={primaryColor}
        name={`question-${question.id}`}
        value={val}
        onChange={e => setVal(e.target.value)}
        disabled={disabled}
        autoFocus
        rows={4}
        placeholder={question.typeOptions?.placeHolder}
        data-cy="text-area-input"
      />
      <SoftPillButton
        primaryColor={primaryColor}
        disabled={disabled || (forceAnswer && !val.trim())}
        onClick={handleSubmit}
        testCy="text-area-next-button"
      >
        {v.next}
      </SoftPillButton>
    </SoftQuestionShell>
  );
};

// -- Slider ---------------------------------------------------------------

export const SoftSliderQuestion: React.FC<SoftQuestionProps> = props => {
  const {
    question,
    progress,
    language,
    primaryColor,
    disabled,
    hidden,
    showPrevious,
    onBack,
    onSelect,
  } = props;
  const v = surveyVerbiage[language];
  const opts = (question.typeOptions || {}) as {
    min: number;
    max: number;
    left?: string;
    right?: string;
    start?: number;
    step?: number;
    notLessThan?: number;
    notMoreThan?: number;
  };
  const min = opts.min ?? 0;
  const max = opts.max ?? 10;
  const step = opts.step || 1;
  const initial =
    typeof opts.notLessThan === 'number'
      ? opts.notLessThan
      : typeof opts.notMoreThan === 'number'
        ? opts.notMoreThan
        : typeof opts.start === 'number'
          ? opts.start
          : min;
  const [value, setValue] = useState<number>(initial);

  const sliderClass = useMemo(
    () => `range-slider__range-${primaryColor.replace('#', '')}`,
    [primaryColor]
  );

  useEffect(() => {
    const id = `slider-styles-${sliderClass}`;
    const existing = document.getElementById(id);

    if (existing) existing.remove();
    const sheet = document.createElement('style');
    sheet.setAttribute('id', id);
    sheet.textContent = `
      .${sliderClass}::-webkit-slider-thumb { appearance: none; width: 22px; height: 22px; border-radius: 50%; background: ${primaryColor}; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
      .${sliderClass}::-moz-range-thumb { width: 22px; height: 22px; border: 0; border-radius: 50%; background: ${primaryColor}; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
      .${sliderClass}:focus { outline: none; }
      .${sliderClass}:focus::-webkit-slider-thumb { box-shadow: 0 0 0 3px white, 0 0 0 6px ${primaryColor}; }
    `;
    document.head.appendChild(sheet);

    return () => sheet.remove();
  }, [sliderClass, primaryColor]);

  const onChangeRange = (event: ChangeEvent<HTMLInputElement>) => {
    const next = Number(event.target.value);

    if (typeof opts.notLessThan === 'number' && next < opts.notLessThan) {
      setValue(opts.notLessThan);

      return;
    }

    if (typeof opts.notMoreThan === 'number' && next > opts.notMoreThan) {
      setValue(opts.notMoreThan);

      return;
    }

    setValue(next);
  };

  const handleSubmit = async () => {
    await onSelect({ type: 'slider', value });
  };

  const fillPct = max === min ? 0 : ((value - min) / (max - min)) * 100;

  return (
    <SoftQuestionShell
      question={question}
      progress={progress}
      language={language}
      primaryColor={primaryColor}
      hidden={hidden}
      showPrevious={showPrevious}
      onBack={onBack}
    >
      <div className="portal-card p-5 flex flex-col gap-4">
        <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
          <span>{opts.left ?? min}</span>
          <span>{opts.right ?? max}</span>
        </div>
        <input
          type="range"
          className={`${sliderClass} w-full appearance-none h-2 rounded-full`}
          value={value}
          min={min}
          max={max}
          step={step}
          onChange={onChangeRange}
          disabled={disabled}
          style={{
            background: `linear-gradient(to right, ${primaryColor} 0%, ${primaryColor} ${fillPct}%, rgba(0,0,0,0.06) ${fillPct}%, rgba(0,0,0,0.06) 100%)`,
          }}
        />
        <p
          className="self-center inline-flex items-center justify-center min-w-12 px-3 py-1 rounded-full text-base font-semibold tabular-nums text-white"
          style={{ backgroundColor: primaryColor }}
        >
          {value}
        </p>
      </div>
      <SoftPillButton primaryColor={primaryColor} disabled={disabled} onClick={handleSubmit}>
        {v.next}
      </SoftPillButton>
    </SoftQuestionShell>
  );
};

// -- Date -----------------------------------------------------------------

export const SoftDateQuestion: React.FC<SoftQuestionProps> = props => {
  const {
    question,
    progress,
    language,
    primaryColor,
    disabled,
    hidden,
    showPrevious,
    onBack,
    onSelect,
  } = props;
  const v = surveyVerbiage[language];
  const [val, setVal] = useState<DateValue | undefined>();

  const handleSubmit = async () => {
    if (!val) return;
    await onSelect({ type: 'date', value: val });
    setVal(undefined);
  };

  return (
    <SoftQuestionShell
      question={question}
      progress={progress}
      language={language}
      primaryColor={primaryColor}
      hidden={hidden}
      showPrevious={showPrevious}
      onBack={onBack}
    >
      <div className="portal-card p-5 flex justify-center">
        <HCDatepicker
          options={question.typeOptions}
          onChange={(e?: DateValue) => setVal(e)}
          primaryColor={primaryColor}
          language={language}
          size="normal"
        />
      </div>
      <SoftPillButton
        primaryColor={primaryColor}
        disabled={disabled || !val}
        onClick={handleSubmit}
        testCy="next-button"
      >
        {v.next}
      </SoftPillButton>
    </SoftQuestionShell>
  );
};
